/** Typed value editors for the batch-edit dialog.
 *
 *  `OpValueEditor` renders the right editor for one mutation (entry type × op):
 *  enum select with labels, boolean select, number field, duration field with
 *  the `1y2M1w3d7h1m33s` grammar, a three-field regex replace editor, or a
 *  plain text field. `ConditionValueEditor` reuses the typed editor for a
 *  condition's right-hand constant when the left path is a known catalog entry.
 */
import { Checkbox, FormControlLabel, MenuItem, Stack, TextField } from '@mui/material';

import type { BatchEditPath } from '@/api/batchEdit';
import { DURATION_OPS, NUMERIC_OPS, STRING_OPS, VALUELESS_OPS, type OpDraft, type RegexValueDraft } from './model';

const MONO = { sx: { fontFamily: 'ui-monospace, monospace' } };

interface TypedValueProps {
  entry: BatchEditPath | null;
  value: unknown;
  onChange: (value: unknown) => void;
  label?: string;
}

/** Editor for a value of the entry's own type (set / set_if_null / condition RHS). */
export function TypedValueEditor({ entry, value, onChange, label = 'Value' }: TypedValueProps) {
  const type = entry?.type ?? 'string';

  if (type === 'enum' && entry?.choices) {
    return (
      <TextField
        select
        label={label}
        size="small"
        fullWidth
        value={value === undefined || value === null ? '' : String(value)}
        onChange={(e) => {
          const choice = entry.choices?.find((c) => String(c.value) === e.target.value);
          onChange(choice ? choice.value : e.target.value);
        }}
      >
        {entry.choices.map((c) => (
          <MenuItem key={String(c.value)} value={String(c.value)}>
            {c.label}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  if (type === 'boolean') {
    return (
      <TextField
        select
        label={label}
        size="small"
        fullWidth
        value={typeof value === 'boolean' ? String(value) : (value as string) || ''}
        onChange={(e) => onChange(e.target.value === 'true')}
      >
        <MenuItem value="true">true</MenuItem>
        <MenuItem value="false">false</MenuItem>
      </TextField>
    );
  }

  if (type === 'integer' || type === 'number') {
    return (
      <TextField
        label={label}
        size="small"
        fullWidth
        type="number"
        inputProps={type === 'integer' ? { step: 1 } : undefined}
        value={value === undefined ? '' : String(value)}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  const placeholder =
    type === 'datetime'
      ? '2026-09-03T10:00:00+00:00'
      : type === 'date'
        ? '2026-09-03'
        : type === 'time'
          ? '10:00:00'
          : undefined;
  return (
    <TextField
      label={label}
      size="small"
      fullWidth
      placeholder={placeholder}
      InputProps={placeholder ? MONO : undefined}
      value={(value as string) ?? ''}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

interface OpValueProps {
  entry: BatchEditPath | null;
  draft: OpDraft;
  onChange: (patch: Partial<OpDraft>) => void;
}

/** Editor for a mutation's value, switching on the operator first. */
export function OpValueEditor({ entry, draft, onChange }: OpValueProps) {
  const op = draft.op;

  if (VALUELESS_OPS.has(op)) return null;

  if (op === 'regex_replace') {
    const patch = (p: Partial<RegexValueDraft>) => onChange({ regex: { ...draft.regex, ...p } });
    return (
      <Stack spacing={1.5}>
        <TextField
          label="Pattern to find (regular expression)"
          size="small"
          fullWidth
          value={draft.regex.pattern}
          onChange={(e) => patch({ pattern: e.target.value })}
          InputProps={MONO}
        />
        <TextField
          label="Replacement"
          size="small"
          fullWidth
          value={draft.regex.replacement}
          onChange={(e) => patch({ replacement: e.target.value })}
          InputProps={MONO}
          helperText="Backreferences allowed: \1, \2, \g<name>. Leave empty to delete the match."
        />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={draft.regex.ignoreCase}
              onChange={(e) => patch({ ignoreCase: e.target.checked })}
            />
          }
          label="Ignore case"
        />
      </Stack>
    );
  }

  if (DURATION_OPS.has(op)) {
    return (
      <TextField
        label="Duration"
        size="small"
        fullWidth
        placeholder="1y2M1w3d7h1m33s"
        InputProps={MONO}
        value={(draft.value as string) ?? ''}
        onChange={(e) => onChange({ value: e.target.value })}
        helperText="y=years, M=months, w=weeks, d=days, h=hours, m=minutes, s=seconds."
      />
    );
  }

  if (NUMERIC_OPS.has(op)) {
    return (
      <TextField
        label="Amount"
        size="small"
        fullWidth
        type="number"
        inputProps={entry?.type === 'integer' ? { step: 1 } : undefined}
        value={draft.value === undefined ? '' : String(draft.value)}
        onChange={(e) => onChange({ value: e.target.value })}
      />
    );
  }

  if (STRING_OPS.has(op)) {
    return (
      <TextField
        label="Text"
        size="small"
        fullWidth
        value={(draft.value as string) ?? ''}
        onChange={(e) => onChange({ value: e.target.value })}
      />
    );
  }

  // set / set_if_null
  return <TypedValueEditor entry={entry} value={draft.value} onChange={(v) => onChange({ value: v })} />;
}
